import { useAuthStore } from '../stores/useAuthStore';
import { useChannelStore } from '../stores/useChannelStore';
import { useChannelMemberStore } from '../stores/useChannelMemberStore';
import { useDmStore } from '../stores/useDmStore';
import { useThreadStore } from '../stores/useThreadStore';
import type { DshSessionScope } from './dsh-session-journal';

export { isDirectMessageSessionScope } from './dsh-session-journal';

export function resolveNormalizedSessionOwner(channelId: string): Pick<DshSessionScope, 'accountId' | 'guildId'> | undefined {
  const accountId = useAuthStore.getState().currentUser?.id;
  if (!accountId || !channelId) return;
  const channels = [...useChannelStore.getState().channels, ...Object.values(useChannelMemberStore.getState().threadChannelsById)];
  const dms = useDmStore.getState().dmChannels;
  const threads = Object.values(useThreadStore.getState().byMessage);
  const visited = new Set<string>();
  function owner(id: string): string | undefined {
    if (visited.has(id)) return;
    visited.add(id);
    const descriptors = channels.filter(channel => channel.id === id);
    const guilds = new Set(descriptors.flatMap(channel => channel.guild_id ? [channel.guild_id] : []));
    if (guilds.size > 1) return;
    const guild = guilds.values().next().value as string | undefined;
    if (dms.some(dm => dm.id === id) || descriptors.some(channel => channel.type === 'dm')) return guild ? undefined : `dm:${accountId}`;
    const parents = new Set([
      ...descriptors.filter(channel => channel.channel_type === 11).flatMap(channel => channel.parent_id ? [channel.parent_id] : []),
      ...threads.filter(thread => thread.id === id).map(thread => thread.parent_channel_id),
    ]);
    if (parents.size > 1) return;
    const parent = parents.values().next().value as string | undefined;
    if (parent) {
      const inherited = owner(parent);
      return inherited && (!guild || guild === inherited) ? inherited : undefined;
    }
    if (descriptors.some(channel => channel.channel_type === 11)) return;
    return guild;
  }
  const guildId = owner(channelId);
  return guildId ? { accountId, guildId } : undefined;
}

export function resolveNormalizedSessionScope(channelId: string, agentId: string): DshSessionScope | undefined {
  const owner = resolveNormalizedSessionOwner(channelId);
  return owner && agentId ? { ...owner, channelId, agentId } : undefined;
}

/** An authenticated mode overrides retained activity from an earlier connection. */
export function normalizedConversationMode(outputMode: 'normalized' | 'legacy' | undefined, observedProviderId?: string): 'normalized' | 'legacy' | 'unknown' {
  return outputMode ?? (observedProviderId ? 'normalized' : 'unknown');
}
